// Curse & veto card definitions + the per-question card-draw rules.
//
// The 21 curses below are the real Jet Lag: Hide+Seek deck (scanned + OCR'd).
// Card face images live in /public/curses/<id>.jpg. The on-screen condition text
// mirrors the card; `{n}` is substituted with the game-size value (see resolveText).
//
// Per the chosen rules: curses DISPLAY (they don't block actions); seekers
// self-clear honor curses, the hider verifies photo curses; timed curses auto-lift.
import type { QType } from "./net";

// ---------------------------------------------------------------------------
// Card-draw rules — when a seeker asks, the hider draws this many cards, keeps 1.
// Vetoing a question instead earns no draw. (Thermometer defaulted to radar tier.)
// ---------------------------------------------------------------------------
export interface DrawRule { draw: number; keep: number }
export const DRAW_RULES: Record<QType, DrawRule> = {
  matching: { draw: 3, keep: 1 },
  measuring: { draw: 3, keep: 1 },
  radar: { draw: 2, keep: 1 },
  thermometer: { draw: 2, keep: 1 },
  photo: { draw: 1, keep: 1 },
};
export function drawRule(type: QType | undefined): DrawRule | null {
  return type && DRAW_RULES[type] ? DRAW_RULES[type] : null;
}
export function drawText(type: QType | undefined): string {
  const r = drawRule(type);
  return r ? `Draw ${r.draw}, keep ${r.keep}` : "Draw cards";
}

// ---------------------------------------------------------------------------
// Game size — the cards' S/M/L values scale with game area. The GM picks per game.
// ---------------------------------------------------------------------------
export type GameSize = "S" | "M" | "L";
export const DEFAULT_SIZE: GameSize = "S";
export const GAME_SIZES: { id: GameSize; label: string; sub: string }[] = [
  { id: "S", label: "Small", sub: "a neighborhood / borough" },
  { id: "M", label: "Medium", sub: "a city" },
  { id: "L", label: "Large", sub: "a region / state" },
];

// ---------------------------------------------------------------------------
// Curse cards.
// ---------------------------------------------------------------------------
export type CurseClear =
  | { mode: "photo"; prompt: string }                       // seeker uploads a photo, hider verifies, then it lifts
  | { mode: "notify"; prompt: string }                      // seeker taps "done" (honor), lifts immediately
  | { mode: "hider" }                                       // only the hider lifts it (they adjudicate)
  | { mode: "timer"; min: { S: number; M: number; L: number } } // auto-lifts after N minutes (size-dependent)
  | { mode: "none" };                                       // lasts the run / no in-app clear

export interface CurseCard {
  id: string;            // slug; image is /curses/<id>.jpg
  name: string;
  emoji: string;
  description: string;   // the effect, shown to the cursed team. May contain `{n}`.
  castingCost: string;   // what the hider pays. May contain `{n}`.
  size?: { S: string; M: string; L: string }; // value substituted for `{n}`
  size2?: { S: string; M: string; L: string }; // a SECOND size value, substituted for `{m}`
  dice?: 1 | 2;          // the cursed team rolls this many dice to comply (shows a 🎲 button)
  clear: CurseClear;
}

export const curseImage = (id: string): string => `/curses/${id}.jpg`;

// Substitute the `{n}` (and optional second `{m}`) tokens with size values.
export function resolveText(text: string, card: CurseCard | null | undefined, size: GameSize): string {
  if (!text) return text;
  const v = card?.size ? card.size[size] : "";
  const v2 = card?.size2 ? card.size2[size] : "";
  return text.replace(/\{n\}/g, v).replace(/\{m\}/g, v2);
}
// The minutes a timer curse lasts, for the current game size.
export function timerMinutes(clear: CurseClear, size: GameSize): number {
  return clear.mode === "timer" ? clear.min[size] : 0;
}

export const CURSES: Record<string, CurseCard> = {
  "overflowing-chalice": {
    id: "overflowing-chalice", name: "Curse of the Overflowing Chalice", emoji: "🍷",
    description: "For the next three questions, you may draw (not keep) an additional card when drawing from the hider deck.",
    castingCost: "Discard a card.",
    clear: { mode: "none" },
  },
  "luxury-car": {
    id: "luxury-car", name: "Curse of the Luxury Car", emoji: "🏎️",
    description: "Take a photo of a car. The seekers must take a photo of a more expensive car before asking another question.",
    castingCost: "A photo of a car.",
    clear: { mode: "photo", prompt: "Photo of a more expensive car" },
  },
  "lemon-phylactery": {
    id: "lemon-phylactery", name: "Curse of the Lemon Phylactery", emoji: "🍋",
    description: "Before asking another question, the seekers must each find a lemon and affix it to the outermost layer of their clothes or skin. If, at any point, one of these lemons is no longer touching a seeker, you are awarded an extra {n} minutes. This curse cannot be played during the endgame.",
    castingCost: "Discard a powerup.",
    size: { S: "10", M: "25", L: "50" },
    clear: { mode: "notify", prompt: "Each seeker has a lemon affixed" },
  },
  "gamblers-feet": {
    id: "gamblers-feet", name: "Curse of the Gambler's Feet", emoji: "🎲",
    description: "For the next {n} minutes, seekers must roll a die before they take any steps in any direction. They may take that many steps before rolling again.",
    castingCost: "Roll a die. If it's an even number, this curse has no effect.",
    size: { S: "20", M: "40", L: "60" },
    dice: 1,
    clear: { mode: "timer", min: { S: 20, M: 40, L: 60 } },
  },
  "egg-partner": {
    id: "egg-partner", name: "Curse of the Egg Partner", emoji: "🥚",
    description: "The seekers must acquire an egg before asking another question. This egg is now treated as an official team member of the seekers. If any team member of the seekers is abandoned or killed (defined as any crack, in the egg's case) before the end of your run, you are awarded an extra {n} minutes. This curse cannot be played during the endgame.",
    castingCost: "Discard two cards.",
    size: { S: "30", M: "45", L: "60" },
    clear: { mode: "notify", prompt: "We've acquired an egg" },
  },
  "endless-tumble": {
    id: "endless-tumble", name: "Curse of the Endless Tumble", emoji: "🌀",
    description: "Seekers must roll a die at least 100 feet and have it land on a 5 or a 6 before they can ask another question. The die must roll the full distance, unaided, using only the momentum from the initial throw and gravity to travel the 100 feet. If the seekers accidentally hit someone with a die, you are awarded a {n} minute bonus.",
    castingCost: "Roll a die. If it's a 5 or 6, this card has no effect.",
    size: { S: "10", M: "20", L: "30" },
    dice: 1,
    clear: { mode: "notify", prompt: "We rolled a 5 or 6 over 100 ft" },
  },
  "cairn": {
    id: "cairn", name: "Curse of the Cairn", emoji: "🪨",
    description: "You have one attempt to stack as many rocks on top of each other as you can in a freestanding tower. Each rock may only touch one other rock. Once added, a rock may not be removed; before adding another, the tower must stand for at least five seconds. If any rock other than the base touches the ground, the tower has fallen — tell the seekers how many rocks high it stood. The seekers must build a rock tower of the same number, under the same rules, before asking another question. The rocks must be found in nature, and both teams must disperse the rocks after.",
    castingCost: "Build a rock tower.",
    clear: { mode: "notify", prompt: "We matched the tower height" },
  },
  "right-turn": {
    id: "right-turn", name: "Curse of the Right Turn", emoji: "➡️",
    description: "For the next {n} minutes, the seekers can only turn right at any street intersection. If they reach a dead end where they cannot continue forward or turn right for another 1,000 feet, they may do a full 180. A right turn is defined as a road at any angle that veers to the right of the seekers.",
    castingCost: "Discard a card.",
    size: { S: "20", M: "40", L: "60" },
    clear: { mode: "timer", min: { S: 20, M: 40, L: 60 } },
  },
  "distant-cuisine": {
    id: "distant-cuisine", name: "Curse of the Distant Cuisine", emoji: "🍽️",
    description: "Find a restaurant within your zone that explicitly serves food from a specific foreign country. The seekers must visit a restaurant serving food from a country that is an equal or greater distance away before asking another question.",
    castingCost: "You must be at the restaurant.",
    clear: { mode: "notify", prompt: "We reached a far-enough restaurant" },
  },
  "bridge-troll": {
    id: "bridge-troll", name: "Curse of the Bridge Troll", emoji: "🌉",
    description: "The seekers must ask their next question from under a bridge.",
    castingCost: "Seekers must be at least {n} miles from you.",
    size: { S: "1", M: "5", L: "30" },
    clear: { mode: "notify", prompt: "We're under a bridge" },
  },
  "urban-explorer": {
    id: "urban-explorer", name: "Curse of the Urban Explorer", emoji: "🚇",
    description: "For the rest of your run, seekers cannot ask questions when they are on transit or in a transit station.",
    castingCost: "Discard 2 cards.",
    clear: { mode: "none" },
  },
  "zoologist": {
    id: "zoologist", name: "Curse of the Zoologist", emoji: "🦎",
    description: "Take a photo of a wild fish, bird, mammal, reptile, amphibian, or bug. The seekers must take a picture of a wild animal in the same category before asking another question.",
    castingCost: "A photo of an animal.",
    clear: { mode: "photo", prompt: "Photo of a wild animal in the same category" },
  },
  "hidden-hangman": {
    id: "hidden-hangman", name: "Curse of the Hidden Hangman", emoji: "🔡",
    description: "Before asking another question or boarding another form of transportation, the seekers must beat the hider in a game of hangman. The hider chooses a 5-letter word; the game ends after a correct word guess or 7 wrong letter guesses (head, body, two arms, two legs, and a hat). The hider must respond to all queries within 30 seconds. The seekers cannot challenge the hider for {n} minutes after a loss.",
    castingCost: "Discard 2 cards.",
    size: { S: "1", M: "2", L: "3" },
    clear: { mode: "hider" },
  },
  "bird-guide": {
    id: "bird-guide", name: "Curse of the Bird Guide", emoji: "🐦",
    description: "You have one chance to film a bird for as long as possible, up to {n} minutes straight. If, at any point, the bird leaves the frame, your timer stops. The seekers must then film a bird for the same amount of time or longer before asking another question.",
    castingCost: "Film a bird.",
    size: { S: "3", M: "10", L: "15" },
    clear: { mode: "photo", prompt: "Video of a bird, matching the hider's time" },
  },
  "impressionable-consumer": {
    id: "impressionable-consumer", name: "Curse of the Impressionable Consumer", emoji: "🛍️",
    description: "Seekers must enter and gain admission (if applicable) to a location, or buy a product, that they saw an advertisement for before asking another question. The advertisement must be found out in the world, not on a seeker's device, and must be at least 100 feet from the product or location itself.",
    castingCost: "The seekers' next question is free.",
    clear: { mode: "notify", prompt: "We visited / bought the advertised thing" },
  },
  "spotty-memory": {
    id: "spotty-memory", name: "Curse of the Spotty Memory", emoji: "🧠",
    description: "For the rest of your run, one random category of questions is disabled at all times. After this curse is played, seekers roll a die to determine the disabled category. It stays disabled until the next question is asked, when a die is rolled again to choose a new category. The same category can be disabled multiple times in a row.",
    castingCost: "Discard a time bonus.",
    clear: { mode: "none" },
  },
  "labyrinth": {
    id: "labyrinth", name: "Curse of the Labyrinth", emoji: "🧩",
    description: "Spend up to {n} minutes drawing a solvable maze and send a photo of it to the seekers (no internet research for designs). The seekers must solve the maze before asking another question.",
    castingCost: "Draw a maze.",
    size: { S: "10", M: "20", L: "30" },
    clear: { mode: "photo", prompt: "Photo of the solved maze" },
  },
  "drained-brain": {
    id: "drained-brain", name: "Curse of the Drained Brain", emoji: "🧟",
    description: "Choose three questions in different categories. The seekers cannot ask those questions for the rest of your run.",
    castingCost: "Discard your hand.",
    clear: { mode: "none" },
  },
  "ransom-note": {
    id: "ransom-note", name: "Curse of the Ransom Note", emoji: "✂️",
    description: "The next question the seekers ask must be composed of words and letters cut out of any printed material. The question must be coherent and include at least 5 words.",
    castingCost: "Spell out “ransom note” as a ransom note (without using this card).",
    clear: { mode: "notify", prompt: "Our ransom-note question is ready" },
  },
  "unguided-tourist": {
    id: "unguided-tourist", name: "Curse of the Unguided Tourist", emoji: "🗺️",
    description: "The hider sends an unzoomed Google Street View image from a street within 500 feet of where the seekers are now (parallel to the horizon, including at least one human-built structure other than a road). Without using the internet, the seekers must find that spot in real life before they can use transportation or ask another question, then send a photo to the hider for verification.",
    castingCost: "Seekers must be outside.",
    clear: { mode: "photo", prompt: "Photo of the Street-View spot, found IRL" },
  },
  "water-weight": {
    id: "water-weight", name: "Curse of Water Weight", emoji: "💧",
    description: "The seekers must acquire and carry at least 2 liters of liquid per seeker for the rest of your run. They cannot ask another question until they have acquired the liquid. The water may be distributed between seekers as they see fit. If the liquid is lost or abandoned at any point after acquisition, the hider is awarded a {n} minute bonus.",
    castingCost: "Seekers must be within 1,000 feet (300 meters) of a body of water.",
    size: { S: "30", M: "30", L: "60" },
    clear: { mode: "notify", prompt: "We've acquired 2 L of liquid per seeker" },
  },
  "jammed-door": {
    id: "jammed-door", name: "Curse of the Jammed Door", emoji: "🚪",
    description: "For the next {n} hours, whenever the seekers want to pass through a doorway into a building, business, train, or other vehicle, they must first roll 2 dice. If they do not roll a 7 or higher, they cannot enter that space (including through other doorways). Any given doorway can be re-attempted after {m} minutes.",
    castingCost: "Discard two cards.",
    size: { S: "0.5", M: "1", L: "3" },
    size2: { S: "5", M: "10", L: "15" },
    dice: 2,
    clear: { mode: "timer", min: { S: 30, M: 60, L: 180 } },
  },
  "u-turn": {
    id: "u-turn", name: "Curse of the U-Turn", emoji: "↩️",
    description: "The seekers must disembark their current mode of transportation at the next station (as long as that station is serviced by another form of transit in the next {n} hours).",
    castingCost: "Seekers must be heading the wrong way (their next station is further from you than they are).",
    size: { S: "0.5", M: "0.5", L: "1" },
    clear: { mode: "notify", prompt: "We disembarked at the next station" },
  },
};

export function curseById(id: string): CurseCard | null {
  return CURSES[id] ?? null;
}
export const ALL_CURSES: CurseCard[] = Object.values(CURSES);

// ---------------------------------------------------------------------------
// An in-flight curse on a specific seeker squad/team. Stored server-side keyed
// by the cursed squad/team key; broadcast to that squad, the hiders, and the GM.
// ---------------------------------------------------------------------------
export type CurseStatus = "active" | "pending-verify" | "cleared";
export interface ActiveCurse {
  instanceId: string; curseId: string; targetKey: string; fromName: string;
  ts: number; status: CurseStatus; clearPhotoKey?: string; clearedTs?: number;
}
export function hasActiveCurse(list: ActiveCurse[] | undefined): boolean {
  return !!list && list.some((c) => c.status !== "cleared");
}
